import React, { useState, useRef, useEffect, useCallback } from 'react';

interface ImageCropModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageSrc: string;
    onSave: (croppedImage: string) => void;
    aspectRatio: number;
    type: 'avatar' | 'banner';
}

interface CropArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
                                                           isOpen,
                                                           onClose,
                                                           imageSrc,
                                                           onSave,
                                                           aspectRatio,
                                                           type
                                                       }) => {
    const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [scale, setScale] = useState(1);
    const [containerOffset, setContainerOffset] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Фиксированные размеры окна редактирования
    const modalSize = {
        width: Math.min(1200, window.innerWidth * 0.9),
        height: Math.min(800, window.innerHeight * 0.9)
    };

    // Размеры области обрезки (финальные размеры)
    const cropSize = type === 'avatar'
        ? { width: 160, height: 160 }
        : { width: 1200, height: 300 };

    // Блокировка скролла при открытии модального окна
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Функция для центрирования изображения в контейнере
    const centerImageInContainer = useCallback((currentScale: number, currentImageSize: { width: number, height: number }) => {
        if (!containerRef.current) return { x: 0, y: 0 };

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        const scaledWidth = currentImageSize.width * currentScale;
        const scaledHeight = currentImageSize.height * currentScale;

        // Центрируем изображение в контейнере
        const x = (containerRect.width - scaledWidth) / 2;
        const y = (containerRect.height - scaledHeight) / 2;

        return {
            x: Math.max(0, x),
            y: Math.max(0, y)
        };
    }, []);

    // Функция для центрирования области обрезки
    const centerCropArea = useCallback((currentScale: number, currentImageSize: { width: number, height: number }, currentOffset: { x: number, y: number }) => {
        const scaledWidth = currentImageSize.width * currentScale;
        const scaledHeight = currentImageSize.height * currentScale;

        // Вычисляем размер области обрезки
        const cropWidth = Math.min(cropSize.width, scaledWidth);
        const cropHeight = cropWidth / aspectRatio;

        // Центрируем область обрезки относительно изображения
        const x = currentOffset.x + (scaledWidth - cropWidth) / 2;
        const y = currentOffset.y + (scaledHeight - cropHeight) / 2;

        return {
            x: Math.max(currentOffset.x, x),
            y: Math.max(currentOffset.y, y),
            width: cropWidth,
            height: cropHeight
        };
    }, [cropSize.width, aspectRatio]);

    // Функция для ограничения области обрезки
    const constrainCropArea = useCallback((currentCropArea: CropArea, currentScale: number, currentImageSize: { width: number, height: number }, currentOffset: { x: number, y: number }) => {
        const scaledWidth = currentImageSize.width * currentScale;
        const scaledHeight = currentImageSize.height * currentScale;

        const minX = currentOffset.x;
        const minY = currentOffset.y;
        const maxX = currentOffset.x + scaledWidth - currentCropArea.width;
        const maxY = currentOffset.y + scaledHeight - currentCropArea.height;

        return {
            ...currentCropArea,
            x: Math.max(minX, Math.min(maxX, currentCropArea.x)),
            y: Math.max(minY, Math.min(maxY, currentCropArea.y)),
            width: Math.min(currentCropArea.width, scaledWidth),
            height: Math.min(currentCropArea.height, scaledHeight)
        };
    }, []);

    const handleImageLoad = useCallback(() => {
        if (imageRef.current && containerRef.current) {
            const img = imageRef.current;

            // Получаем размеры изображения
            const naturalWidth = img.naturalWidth;
            const naturalHeight = img.naturalHeight;
            setImageSize({ width: naturalWidth, height: naturalHeight });

            // Размеры контейнера для изображения
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const containerWidth = containerRect.width - 40;
            const containerHeight = containerRect.height - 40;

            // Вычисляем начальный масштаб чтобы изображение поместилось в контейнер
            const scaleX = containerWidth / naturalWidth;
            const scaleY = containerHeight / naturalHeight;
            const initialScale = Math.min(scaleX, scaleY, 1);

            setScale(initialScale);

            // Центрируем изображение в контейнере
            const offset = centerImageInContainer(initialScale, { width: naturalWidth, height: naturalHeight });
            setContainerOffset(offset);

            // Центрируем область обрезки
            const centeredCrop = centerCropArea(initialScale, { width: naturalWidth, height: naturalHeight }, offset);
            setCropArea(centeredCrop);
        }
    }, [centerImageInContainer, centerCropArea]);

    const handleMouseDown = (e: React.MouseEvent, action: 'move' | 'resize') => {
        e.preventDefault();
        e.stopPropagation();

        if (action === 'move') {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - cropArea.x,
                y: e.clientY - cropArea.y
            });
        } else if (action === 'resize') {
            setIsResizing(true);
            setDragStart({
                x: e.clientX,
                y: e.clientY
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging && !isResizing) return;

        if (isDragging) {
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;

            setCropArea(prev => ({
                ...prev,
                x: newX,
                y: newY
            }));
        } else if (isResizing) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;

            const delta = Math.max(deltaX, deltaY);
            const newWidth = Math.max(
                cropSize.width * 0.3,
                Math.min(
                    cropSize.width,
                    cropArea.width + delta
                )
            );
            const newHeight = newWidth / aspectRatio;

            const finalWidth = Math.max(
                cropSize.width * 0.3,
                Math.min(newWidth, imageSize.width * scale)
            );
            const finalHeight = finalWidth / aspectRatio;

            setCropArea(prev => ({
                ...prev,
                width: finalWidth,
                height: finalHeight
            }));

            setDragStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        if (isDragging || isResizing) {
            const constrainedCrop = constrainCropArea(cropArea, scale, imageSize, containerOffset);
            setCropArea(constrainedCrop);
        }
        setIsDragging(false);
        setIsResizing(false);
    };

    const handleScaleChange = (newScale: number) => {
        const minScale = 0.1;
        const maxScale = 3;
        const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));

        // Центрируем изображение при изменении масштаба
        const newOffset = centerImageInContainer(clampedScale, imageSize);
        setContainerOffset(newOffset);

        let newCropArea = { ...cropArea };

        if (clampedScale < scale) {
            const maxPossibleWidth = Math.min(cropSize.width, imageSize.width * clampedScale);
            const maxPossibleHeight = maxPossibleWidth / aspectRatio;

            if (newCropArea.width > maxPossibleWidth) {
                newCropArea.width = maxPossibleWidth;
                newCropArea.height = maxPossibleHeight;
            }
        }

        // Центрируем область обрезки относительно нового положения изображения
        newCropArea = centerCropArea(clampedScale, imageSize, newOffset);

        setScale(clampedScale);
        setCropArea(newCropArea);
    };

    const handleSave = () => {
        if (!imageRef.current) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Увеличиваем размер канваса для лучшего качества
        const scaleFactor = 2; // Увеличиваем в 2 раза для Retina дисплеев
        canvas.width = cropSize.width * scaleFactor;
        canvas.height = cropSize.height * scaleFactor;

        // Устанавливаем высокое качество рендеринга
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        const img = imageRef.current;

        // Правильное вычисление координат обрезки
        const scaleRatio = 1 / scale;

        // Координаты относительно изображения (без учета смещения контейнера)
        const sx = (cropArea.x - containerOffset.x) * scaleRatio;
        const sy = (cropArea.y - containerOffset.y) * scaleRatio;
        const sWidth = cropArea.width * scaleRatio;
        const sHeight = cropArea.height * scaleRatio;

        // Проверяем что координаты в пределах изображения
        if (sx < 0 || sy < 0 || sx + sWidth > imageSize.width || sy + sHeight > imageSize.height) {
            alert('Область обрезки выходит за пределы изображения');
            return;
        }

        // Рисуем с увеличенным размером для лучшего качества
        ctx.drawImage(
            img,
            sx, sy, sWidth, sHeight,           // source rectangle
            0, 0, canvas.width, canvas.height  // destination rectangle
        );

        // Сохраняем с максимальным качеством
        const croppedImage = canvas.toDataURL('image/jpeg', 1.0); // 100% качество

        onSave(croppedImage);
    };

    const handleCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay-crop">
            <div
                className="image-crop-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: `${modalSize.width}px`,
                    height: `${modalSize.height}px`
                }}
            >
                <div className="modal-header">
                    <h3>Редактирование {type === 'avatar' ? 'аватара' : 'баннера'}</h3>
                    <button className="close-btn" onClick={handleCancel}>×</button>
                </div>

                <div className="crop-container-wrapper">
                    <div
                        ref={containerRef}
                        className="crop-container"
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        <img
                            ref={imageRef}
                            src={imageSrc}
                            alt="Crop preview"
                            style={{
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left',
                                position: 'absolute',
                                left: containerOffset.x,
                                top: containerOffset.y
                            }}
                            onLoad={handleImageLoad}
                            onDragStart={(e) => e.preventDefault()}
                        />
                        <div
                            className="crop-area"
                            style={{
                                left: cropArea.x,
                                top: cropArea.y,
                                width: cropArea.width,
                                height: cropArea.height
                            }}
                            onMouseDown={(e) => handleMouseDown(e, 'move')}
                        >
                            <div className="crop-guide">
                                <div className="guide-vertical"></div>
                                <div className="guide-horizontal"></div>
                            </div>
                            <div
                                className="resize-handle"
                                onMouseDown={(e) => handleMouseDown(e, 'resize')}
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-controls">
                    <div className="zoom-controls">
                        <button
                            type="button"
                            onClick={() => handleScaleChange(scale - 0.1)}
                        >
                            -
                        </button>
                        <span>{(scale * 100).toFixed(0)}%</span>
                        <button
                            type="button"
                            onClick={() => handleScaleChange(scale + 0.1)}
                        >
                            +
                        </button>
                    </div>

                    <div className="size-info">
                        Область: {Math.round(cropArea.width)}×{Math.round(cropArea.height)}
                    </div>

                    <div className="action-buttons">
                        <button className="profile-cancel-btn" onClick={handleCancel}>
                            Отмена
                        </button>
                        <button className="profile-save-btn" onClick={handleSave}>
                            Сохранить
                        </button>
                    </div>
                </div>

                <div className="modal-hint">
                    <p>• Перетаскивайте область обрезки для позиционирования</p>
                    <p>• Тяните за угол для изменения размера области обрезки</p>
                    <p>• Используйте кнопки +/- для масштабирования изображения</p>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;